"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import type { Product } from "@/components/buy/product-types";
import { useCart } from "@/components/cart/cart-context";
import { CustomizePreviewGallery, type CalibrationImage } from "@/components/customize/customize-preview-gallery";
import { GROUPS, modelUrlForFile, type GroupKey } from "@/components/three/basalt-mount-model";
import { COUNTRY_COLOR_PRESETS, countryCodeFromFlag, DEFAULT_COLOR_PATTERN } from "@/lib/country-color-presets";

type ColorOption = { id: string; name: string; swatchHex: string };
type ComponentOption = { id: string; name: string };

const FITMENTS = ["23mm", "25mm"];

// Maps a real dbo.ProductComponents.Name ("Cap", "Main Body", "Locker", "Screw Cap") onto the 3D model's
// own group key (A/B/C/D) -- normalized (strip spaces/underscores, lowercase) since the component names
// use spaces and the GLB's own mesh names use underscores ("Main_Body"), but both describe the same 4
// physical parts.
function groupKeyForComponentName(name: string): GroupKey | undefined {
  const normalized = name.replace(/[\s_]/g, "").toLowerCase();
  return GROUPS.find((g) => g.meshName.replace(/[\s_]/g, "").toLowerCase() === normalized)?.key;
}

const NEUTRAL_PREVIEW_COLOR = "#8E9089"; // shown for any part not yet chosen -- Bambu "Gray," a neutral placeholder rather than an arbitrary/alarming color

export function CustomizeExperience({
  products, colors, components, surcharge, initialFitment, calibrationImages, activeModelFile,
}: {
  products: Product[];
  colors: ColorOption[];
  components: ComponentOption[];
  surcharge: number;
  initialFitment: string | null;
  calibrationImages: CalibrationImage[];
  // Bare .glb filename from the admin-configurable ThreeD:ActiveModelFile setting (see
  // CommerceSettingsController) -- resolved to a real /models/... URL below via modelUrlForFile.
  activeModelFile: string;
}) {
  const [fitment, setFitment] = useState(initialFitment && FITMENTS.includes(initialFitment) ? initialFitment : FITMENTS[0]);
  const [selections, setSelections] = useState<Record<string, string>>({});
  // Starts closed, not components[0]?.id -- that used to open the first tile immediately so a shopper with
  // nothing chosen yet had something prompting them, but the geo-IP/default-pattern effect below now
  // always fills every part on mount regardless (a real preset match, or DEFAULT_COLOR_PATTERN as the
  // fallback), so that prompt state never actually reflects reality anymore -- it just flashed open then
  // snapped shut once the effect's own setOpenComponentId(null) ran a moment later, confirmed directly
  // from a real first-load screenshot. Starting closed matches the post-effect state exactly, so there's
  // nothing left to flash.
  const [openComponentId, setOpenComponentId] = useState<string | null>(null);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addItem } = useCart();
  const router = useRouter();

  const selectedProduct = useMemo(() => products.find((p) => p.fitment === fitment) ?? null, [products, fitment]);
  const allSelected = components.length > 0 && components.every((c) => selections[c.id]);
  const totalPrice = selectedProduct ? selectedProduct.unitPrice + surcharge : null;

  // Drives the live 3D preview -- every component maps to a group key (A/B/C/D); parts not yet chosen
  // fall back to a neutral gray rather than leaving that part uncolored/invisible in the render.
  const groupColors = useMemo(() => {
    const result: Record<GroupKey, string> = { A: NEUTRAL_PREVIEW_COLOR, B: NEUTRAL_PREVIEW_COLOR, C: NEUTRAL_PREVIEW_COLOR, D: NEUTRAL_PREVIEW_COLOR };
    for (const component of components) {
      const key = groupKeyForComponentName(component.name);
      if (!key) continue;
      const chosen = colors.find((c) => c.id === selections[component.id]);
      if (chosen) result[key] = chosen.swatchHex;
    }
    return result;
  }, [components, colors, selections]);

  const chooseColor = (componentId: string, colorId: string) => {
    setActivePreset(null); // no longer reflects any single preset once diverged
    setSelections((prev) => ({ ...prev, [componentId]: colorId }));
    // Advance to the next not-yet-colored tile, car-configurator style -- or collapse if that was the last one.
    const remaining = components.filter((c) => c.id !== componentId && !selections[c.id]);
    setOpenComponentId(remaining[0]?.id ?? null);
  };

  // Shared by applyPreset below and the geo-IP default effect further down -- both are ultimately just
  // "resolve these part->color-name pairs against the real Colors catalog and select them," the only
  // difference being whether it's a named country preset (updates the dropdown) or a plain fallback
  // pattern (doesn't correspond to any single dropdown option).
  const applyColorsByName = (colorNames: Record<GroupKey, string>, opts: { onlyIfEmpty?: boolean } = {}) => {
    setSelections((prev) => {
      if (opts.onlyIfEmpty && Object.keys(prev).length > 0) return prev; // user already picked something -- don't clobber it
      const next = { ...prev };
      for (const component of components) {
        const key = groupKeyForComponentName(component.name);
        if (!key) continue;
        const match = colors.find((c) => c.name === colorNames[key]);
        if (match) next[component.id] = match.id;
      }
      return next;
    });
  };

  const applyPreset = (preset: (typeof COUNTRY_COLOR_PRESETS)[number]) => {
    setActivePreset(preset.name);
    applyColorsByName(preset.colors);
    setOpenComponentId(null);
  };

  // Defaults the color selection to the shopper's own country's flag pattern on first load -- e.g. a
  // visitor from Italy sees the Italy preset already applied, not four blank/neutral-gray tiles. Uses the
  // same /api/geo endpoint (Cloudflare's CF-IPCountry, forwarded by Caddy) the checkout address form
  // already reads for its own country default -- see checkout-form.tsx's identical geo-IP effect. Falls
  // back to DEFAULT_COLOR_PATTERN (black/red/red/black) when there's no country match at all (geo lookup
  // failed, e.g. plain http://localhost with no Cloudflare in front, or a country with no preset defined).
  // onlyIfEmpty guards against clobbering a selection the shopper already made in the brief async gap
  // before this resolves. setOpenComponentId(null) after applying -- openComponentId's own initial state
  // is components[0]?.id (so the first tile starts open when nothing's chosen yet, prompting a pick), but
  // once this effect has auto-filled every part that's no longer true, and nothing else was closing it:
  // applyPreset (the manual country-preset dropdown) already does this, but a shopper who never touches
  // that dropdown -- the common case, since this effect runs for everyone on mount -- saw the first tile
  // sitting open despite already having a complete, colored build. Confirmed directly: first real page
  // load landed with the Cap tile expanded even though all 4 parts already had colors.
  useEffect(() => {
    (async () => {
      let countryCode: string | null = null;
      try {
        const response = await fetch("/api/geo");
        const data = await response.json();
        countryCode = data.countryCode ?? null;
      } catch {
        // geo lookup failing is not fatal -- falls through to the plain default pattern below
      }
      const preset = countryCode ? COUNTRY_COLOR_PRESETS.find((p) => countryCodeFromFlag(p.flag) === countryCode) : undefined;
      if (preset) {
        setActivePreset(preset.name);
        applyColorsByName(preset.colors, { onlyIfEmpty: true });
      } else {
        applyColorsByName(DEFAULT_COLOR_PATTERN, { onlyIfEmpty: true });
      }
      setOpenComponentId(null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only, same as checkout-form.tsx's geo effect
  }, []);

  const buildAndAddToCart = async (options?: { openCartOnAdd?: boolean }): Promise<boolean> => {
    if (!selectedProduct || !allSelected) return false;
    setError(null);
    try {
      const customizationResponse = await fetch("/api/customizations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tier: "Custom",
          componentColors: components.map((c) => ({ componentId: c.id, colorId: selections[c.id] })),
        }),
      });
      if (!customizationResponse.ok) {
        setError("Couldn't prepare that configuration. Please try again.");
        return false;
      }
      const customization = await customizationResponse.json();
      await addItem(selectedProduct.id, customization.id, 1, options);
      return true;
    } catch {
      setError("Couldn't add that to your cart. Please try again.");
      return false;
    }
  };

  const handleAddToCart = async () => {
    setAdding(true);
    await buildAndAddToCart();
    setAdding(false);
  };

  const handleBuyNow = async () => {
    setAdding(true);
    // Heading straight to checkout -- popping the cart drawer open first would just flash and vanish
    // (worst on mobile, where it's a large fixed-position panel), so skip it here.
    const ok = await buildAndAddToCart({ openCartOnAdd: false });
    setAdding(false);
    if (ok) router.push("/checkout");
  };

  return (
    <div className="customize-layout">
      <div className="customize-preview">
        <CustomizePreviewGallery
          images={calibrationImages} groupColors={groupColors} fallbackImageSrc="/images/hero-mount.jpg"
          modelPath={modelUrlForFile(activeModelFile)}
        />
        <div className="customize-preview-summary">
          <h2 className="checkout-section-title">Your build</h2>
          <p className="mono">{fitment}</p>
          <ul>
            {components.map((component) => {
              const chosen = colors.find((c) => c.id === selections[component.id]);
              return (
                <li key={component.id}>
                  {chosen && <span className="admin-swatch-preview" style={{ background: chosen.swatchHex }} />}
                  {component.name}: {chosen?.name ?? "not set"}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="customize-config">
        <span className="word">BUILD YOURS</span>
        <p className="form-hint">
          Pick a color for every part — this build costs {selectedProduct?.currency ?? "AUD"} $
          {surcharge.toFixed(2)} more than the basic single-color mount, since each part is printed and
          finished separately.
        </p>

        <div className="buy-field customize-size-field">
          <span className="lbl">Size</span>
          <div className="pills">
            {FITMENTS.map((f) => (
              <button key={f} type="button" className={`pill${fitment === f ? " sel" : ""}`} onClick={() => setFitment(f)}>
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="buy-field customize-preset-field">
          <span className="lbl">National color preset</span>
          <select
            value={activePreset ?? ""}
            onChange={(e) => {
              const preset = COUNTRY_COLOR_PRESETS.find((p) => p.name === e.target.value);
              if (preset) applyPreset(preset);
            }}
            className="customize-preset-select"
          >
            <option value="" disabled>Pick a country flag pattern…</option>
            {COUNTRY_COLOR_PRESETS.slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((preset) => (
                <option key={preset.name} value={preset.name}>{preset.flag} {preset.name}</option>
              ))}
          </select>
        </div>

        <div className="customize-tiles">
          {components.map((component) => {
            const chosenColorId = selections[component.id];
            const chosen = colors.find((c) => c.id === chosenColorId);
            const isOpen = openComponentId === component.id;
            return (
              <div key={component.id} className={isOpen ? "customize-tile open" : "customize-tile"}>
                <button type="button" className="customize-tile-header" onClick={() => setOpenComponentId(isOpen ? null : component.id)}>
                  <span>{component.name}</span>
                  <span className="customize-tile-status">
                    {chosen && <span className="admin-swatch-preview" style={{ background: chosen.swatchHex }} />}
                    {chosen?.name ?? "Choose a color"}
                  </span>
                </button>
                {isOpen && (
                  <div className="color-picker">
                    {colors.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className={`color-swatch${chosenColorId === c.id ? " selected" : ""}`}
                        style={{ background: c.swatchHex }}
                        onClick={() => chooseColor(component.id, c.id)}
                        aria-label={c.name}
                        aria-pressed={chosenColorId === c.id}
                        title={c.name}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {selectedProduct && totalPrice !== null && (
          <div className="price mono">
            {selectedProduct.currency} ${totalPrice.toFixed(2)}
          </div>
        )}

        {error && <p className="cart-page-error">{error}</p>}

        <div className="buy-actions">
          <button type="button" className="btn-secondary" onClick={handleAddToCart} disabled={!allSelected || !selectedProduct || adding}>
            {adding ? "Adding…" : "Add to Cart"}
          </button>
          <button type="button" className="btn-primary" onClick={handleBuyNow} disabled={!allSelected || !selectedProduct || adding}>
            {adding ? "Adding…" : "Buy Now"}
          </button>
        </div>
      </div>
    </div>
  );
}
